import React, { useState } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { createRequestOffer, getBuySellFormMode, getSimplifiedFormEnabled, getOrganizationBySlug } from 'wasp/client/operations';
import { SimplifiedSubmitForm, SubmissionModeSwitch } from '../components/SimplifiedSubmit';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { locationSectionPathFromLocation } from '../../client/lib/locationPaths';
import {
  ArrowLeft,
  ArrowRight,
  HandHeart,
  DollarSign,
  Tag,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Package,
  Star,
  Gift,
  CheckCircle2,
  ArrowLeftRight,
  User
} from 'lucide-react';

interface RequestOfferFormData {
  title: string;
  description: string;
  price: string;
  category: 'REQUEST' | 'OFFER';
  condition: 'NEW' | 'LIKE_NEW' | 'GOOD' | 'FAIR' | 'POOR';
  contactInfo: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const REQUEST_OFFER_CATEGORIES = [
  { value: 'OFFER', label: 'Offer', icon: Gift, description: 'I want to offer something' },
  { value: 'REQUEST', label: 'Request', icon: HandHeart, description: 'I want to request something' }
];

const CONDITION_OPTIONS = [
  { value: 'NEW', label: 'New', description: 'Brand new, never used' },
  { value: 'GOOD', label: 'Good', description: 'Good condition, some wear' },
  { value: 'FAIR', label: 'Used', description: 'Noticeable wear, fully functional' }
] as const;

const STEPS = [
  { id: 1, label: 'Posting Type', icon: ArrowLeftRight, description: 'What are you posting?' },
  { id: 2, label: 'Item Details', icon: Package, description: 'Describe your item' },
  { id: 3, label: 'Pricing', icon: DollarSign, description: 'Set a price' },
  { id: 4, label: 'Contact & Review', icon: User, description: 'Finalize and submit' }
];

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({ currentStep }: { currentStep: number }) => (
  <div className="w-full mb-8">
    <div className="relative flex items-center justify-between">
      <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
        <div className="h-full bg-blue-500 transition-all duration-500 ease-out" style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }} />
      </div>
      {STEPS.map((step) => {
        const Icon = step.icon;
        const isDone = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        return (
          <div key={step.id} className="relative flex flex-col items-center z-10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isDone ? 'bg-blue-500 border-blue-500 text-white' : isCurrent ? 'bg-white border-blue-500 text-blue-600 shadow-md shadow-blue-100' : 'bg-white border-gray-200 text-gray-400'}`}>
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <span className={`mt-2 text-xs font-medium hidden sm:block ${isCurrent ? 'text-blue-600' : isDone ? 'text-blue-400' : 'text-gray-400'}`}>{step.label}</span>
          </div>
        );
      })}
    </div>
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-500">Step {currentStep} of {STEPS.length} — <span className="font-medium text-gray-700">{STEPS[currentStep - 1].description}</span></p>
    </div>
  </div>
);

function WizardForm({
  formData,
  errors,
  isSubmitting,
  handleChange,
  handleSubmit,
  handleGoBack,
  selectedLocationName,
  modeSwitch
}: {
  formData: RequestOfferFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  handleChange: (field: keyof RequestOfferFormData, value: any) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleGoBack: () => void;
  selectedLocationName: string;
  modeSwitch?: React.ReactNode;
}) {
  const [step, setStep] = useState(1);
  const canAdvanceFrom1 = formData.title.trim().length > 0;
  const canAdvanceFrom2 = formData.description.trim().length > 0;

  const handleNext = () => {
    if (step === 1 && !canAdvanceFrom1) return;
    if (step === 2 && !canAdvanceFrom2) return;
    setStep(s => Math.min(s + 1, STEPS.length));
  };

  const handleBack = () => step === 1 ? handleGoBack() : setStep(s => Math.max(s - 1, 1));

  const QUICK_PRICES = [
    { label: 'Free', value: '0' },
    { label: '$5', value: '5' },
    { label: '$10', value: '10' },
    { label: '$25', value: '25' },
    { label: '$50', value: '50' },
    { label: '$100', value: '100' }
  ];

  return (
    <DashboardLayout>
      {modeSwitch}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <ArrowLeftRight className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Post Request / Offer</h1>
            <p className="text-sm text-gray-500">{selectedLocationName}</p>
          </div>
        </div>

        <StepIndicator currentStep={step} />

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">

          {/* Step 1: Posting Type + Title */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-3">What type of posting is this?</h2>
                <div className="grid grid-cols-2 gap-3">
                  {/* Request card */}
                  <button
                    type="button"
                    onClick={() => handleChange('category', 'REQUEST')}
                    className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                      formData.category === 'REQUEST'
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    {formData.category === 'REQUEST' && (
                      <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-blue-500" />
                    )}
                    <div className="font-semibold text-gray-900 mb-1">Request</div>
                    <div className="text-xs text-gray-500">I'm looking for something</div>
                  </button>

                  {/* Offer card */}
                  <button
                    type="button"
                    onClick={() => handleChange('category', 'OFFER')}
                    className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                      formData.category === 'OFFER'
                        ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    {formData.category === 'OFFER' && (
                      <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-green-500" />
                    )}
                    <div className="font-semibold text-gray-900 mb-1">Offer</div>
                    <div className="text-xs text-gray-500">I have something to give</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.title ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Looking for a laptop, Offering tutoring services"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.title}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Description + Condition */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    handleChange('description', e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  rows={4}
                  maxLength={255}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none overflow-hidden ${
                    errors.description ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Provide detailed information about what you're requesting or offering..."
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.description.length}/255 characters</p>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                <div className="flex gap-3 flex-wrap">
                  {CONDITION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleChange('condition', opt.value)}
                      className={`relative flex-1 min-w-[90px] border-2 rounded-xl p-3 text-left transition-all duration-200 ${
                        formData.condition === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {formData.condition === opt.value && (
                        <CheckCircle2 className="absolute top-2 right-2 w-3.5 h-3.5 text-blue-500" />
                      )}
                      <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Pricing */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value ? String(parseInt(e.target.value, 10)) : '')}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-lg"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-2">Quick pick</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PRICES.map((q) => (
                    <button
                      key={q.value}
                      type="button"
                      onClick={() => handleChange('price', q.value)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-all duration-150 ${
                        formData.price === q.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                Set a fair price to attract the right people. Leave blank or set to 0 if free.
              </div>
            </div>
          )}

          {/* Step 4: Contact & Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
                <input
                  type="text"
                  value={formData.contactInfo}
                  onChange={(e) => handleChange('contactInfo', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Phone number or email"
                />
                <p className="text-xs text-gray-400 mt-1">Pre-filled from your account — edit if needed.</p>
              </div>

              {/* Summary card */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Summary</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium text-gray-900">{formData.category === 'REQUEST' ? 'Request' : 'Offer'}</span>
                  <span className="text-gray-500">Title</span>
                  <span className="font-medium text-gray-900 break-words">{formData.title || '—'}</span>
                  <span className="text-gray-500">Condition</span>
                  <span className="font-medium text-gray-900">
                    {CONDITION_OPTIONS.find(c => c.value === formData.condition)?.label ?? formData.condition}
                  </span>
                  <span className="text-gray-500">Price</span>
                  <span className="font-medium text-gray-900">
                    {formData.price ? `$${formData.price}` : 'Not set'}
                  </span>
                </div>
              </div>

              {/* Approval notice */}
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>All postings go through an approval process before being published.</span>
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.submit}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < STEPS.length ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={(step === 1 && !canAdvanceFrom1) || (step === 2 && !canAdvanceFrom2)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => handleSubmit(e as any)}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Posting...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Submit
                </>
              )}
            </button>
          )}
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-1.5 mt-5">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={`rounded-full transition-all duration-300 ${
                step === s.id ? 'w-4 h-2 bg-blue-500' : step > s.id ? 'w-2 h-2 bg-blue-300' : 'w-2 h-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function AddRequestOfferPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { data: org } = useQuery(getOrganizationBySlug, { slug: slug! }, { enabled: !!slug }) as { data: any };

  const { data: formMode, isLoading: formModeLoading } = useQuery(getBuySellFormMode);
  const { data: simplifiedEnabled, isLoading: simplifiedEnabledLoading } = useQuery(getSimplifiedFormEnabled);

  const createRequestOfferFn = useAction(createRequestOffer);

  const [submissionMode, setSubmissionMode] = useState<'full' | 'simplified'>('simplified');

  const defaultContact = [(user as any)?.phoneNumber, user?.email].filter(Boolean).join(' · ');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RequestOfferFormData>({
    title: '',
    description: '',
    price: '',
    category: 'OFFER',
    condition: 'GOOD',
    contactInfo: defaultContact,
    visibility: 'VERIFIED'
  });

  const handleChange = (field: keyof RequestOfferFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createRequestOfferFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        organizationId: org?.id,
        price: formData.price ? parseFloat(formData.price) : undefined,
        category: formData.category,
        condition: formData.condition,
        contactInfo: formData.contactInfo.trim(),
        visibility: formData.visibility,
        submissionType: 'STANDARD'
      });

      // Navigate back to the request-offer page
      handleGoBack();
    } catch (error) {
      console.error('Error creating request/offer item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(locationSectionPathFromLocation(selectedLocation, 'request-offer'));
  };

  const locationName = selectedLocation?.name || 'No location selected';
  const showModeSwitch = !!simplifiedEnabled;
  const effectiveMode: 'full' | 'simplified' = showModeSwitch ? submissionMode : 'full';

  const handleSimplifiedSubmit = async (description: string) => {
    if (!selectedLocation?.id) return;
    setIsSubmitting(true);
    try {
      const shortTitle = description.length > 60 ? description.slice(0, 57) + '…' : description;
      await createRequestOfferFn({
        title: shortTitle, description, locationId: selectedLocation.id,
        price: undefined, category: 'OFFER', condition: 'GOOD',
        contactInfo: defaultContact, visibility: 'VERIFIED',
        submissionType: 'QUICK'
      });
      handleGoBack();
    } catch (error) {
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to post request/offer items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (formModeLoading || simplifiedEnabledLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (effectiveMode === 'simplified') {
    return (
      <DashboardLayout>
        <div className="overflow-y-auto h-full bg-gray-50">
          {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
          <SimplifiedSubmitForm isSubmitting={isSubmitting} onSubmit={handleSimplifiedSubmit} selectedLocationName={locationName} />
        </div>
      </DashboardLayout>
    );
  }

  if (formMode === 'wizard') {
    return (
      <WizardForm
        formData={formData}
        errors={errors}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        handleGoBack={handleGoBack}
        selectedLocationName={locationName}
        modeSwitch={showModeSwitch ? <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} /> : undefined}
      />
    );
  }

  return (
    <DashboardLayout>
      {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <HandHeart className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Post Request/Offer Item</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="request-offer-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Request/Offer Type Selection */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What are you posting?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {REQUEST_OFFER_CATEGORIES.map((category) => {
                        const IconComponent = category.icon;
                        return (
                          <label
                            key={category.value}
                            className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              formData.category === category.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="category"
                              value={category.value}
                              checked={formData.category === category.value}
                              onChange={(e) => handleChange('category', e.target.value as 'REQUEST' | 'OFFER')}
                              className="sr-only"
                            />
                            <div className="flex items-center gap-3">
                              <IconComponent className={`w-6 h-6 ${formData.category === category.value ? 'text-blue-600' : 'text-gray-400'}`} />
                              <div>
                                <div className="font-medium text-gray-900">{category.label}</div>
                                <div className="text-sm text-gray-500">{category.description}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Item Title */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Item Details</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="e.g., Looking for a laptop, Offering tutoring services"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Provide detailed information about what you're requesting or offering..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Condition */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Condition</label>
                      <select
                        value={formData.condition}
                        onChange={(e) => handleChange('condition', e.target.value as 'NEW' | 'LIKE_NEW' | 'GOOD' | 'FAIR' | 'POOR')}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      >
                        {CONDITION_OPTIONS.map(condition => (
                          <option key={condition.value} value={condition.value}>
                            {condition.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Price</span>
                          <Tooltip content="Leave empty if you prefer to negotiate or if it's free">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={(e) => handleChange('price', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Contact Information</span>
                          <Tooltip content="How others can reach you">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.contactInfo}
                          onChange={(e) => handleChange('contactInfo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Phone number or email"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="request-offer-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Tips for Better Results */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tips for Better Results</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Use clear, descriptive titles that include key details</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Include detailed descriptions of what you need or are offering</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Set a fair price or mention if it's free/negotiable</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Provide accurate contact information for easy communication</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Be responsive to inquiries to increase your chances of success</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="request-offer-form"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}



